import React, { useState, useEffect, useCallback } from 'react';
import { ethers, ZeroAddress } from 'ethers';
import abi from '../abi/Lending.json';
import { FaPlus, FaUserPlus, FaMoneyCheckAlt, FaHandHoldingUsd, FaSearchDollar, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const CONTRACT_ADDRESS = "0x8A6Db722557b8e12AFD3cd8A9fB0C129DBff295C";

function Lending({ provider, account, onTransaction }) {
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [supportedTokens, setSupportedTokens] = useState([]);
  const [activeTab, setActiveTab] = useState('create');
  const [lenderLoans, setLenderLoans] = useState([]);
  const [borrowerLoans, setBorrowerLoans] = useState([]);
  const [availableLoans, setAvailableLoans] = useState([]);
  const [loanToken, setLoanToken] = useState('');
  const [collateralToken, setCollateralToken] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanDuration, setLoanDuration] = useState('');
  const [collateralPercentage, setCollateralPercentage] = useState('');
  const [collateralAmount, setCollateralAmount] = useState('');
  const [lenderAddress, setLenderAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSupportedTokens = useCallback(async (contr) => {
    const tokens = [
      ZeroAddress, // ETH
      '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // USDC
      '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', // wBTC
    ];
    const erc20Abi = [
      'function symbol() external view returns (string)',
      'function decimals() external view returns (uint8)',
    ];
    const data = [];

    for (let tok of tokens) {
      try {
        const info = await contr.supportedTokens(tok);
        if (info.isSupported) {
          let symbol = tok === ZeroAddress ? 'ETH' : null;
          if (!symbol) {
            try {
              const tokenContract = new ethers.Contract(tok, erc20Abi, provider);
              symbol = await tokenContract.symbol();
            } catch (err) {
              symbol = `TKN-${tok.slice(-4)}`;
              console.warn(`Failed to fetch symbol for token ${tok}:`, err.message);
            }
          }
          data.push({
            tokenAddress: tok,
            tokenDecimals: Number(info.tokenDecimals),
            symbol,
            priceFeed: info.priceFeed,
            isSupported: info.isSupported,
          });
        }
      } catch (err) {
        console.warn(`Token ${tok} not supported:`, err.message);
      }
    }
    console.log('Supported tokens:', data); // Logování pro ověření načtených tokenů
    setSupportedTokens(data);
  }, [provider]);

  const listenToEvents = useCallback((contr) => {
    if (!contr) return;

    contr.on('UserLoanCreated', async (...args) => {
      await fetchLoans(contr, account);
      if (onTransaction) onTransaction({ hash: args[args.length - 1].transactionHash, type: 'Loan Created' });
    });
    contr.on('UserLoanActivated', async (...args) => {
      await fetchLoans(contr, account);
      if (onTransaction) onTransaction({ hash: args[args.length - 1].transactionHash, type: 'Loan Activated' });
    });
    contr.on('UserLoanRepaid', async (...args) => {
      await fetchLoans(contr, account);
      if (onTransaction) onTransaction({ hash: args[args.length - 1].transactionHash, type: 'Loan Repaid' });
    });
    contr.on('UserLoanLiquidated', async (...args) => {
      await fetchLoans(contr, account);
      if (onTransaction) onTransaction({ hash: args[args.length - 1].transactionHash, type: 'Loan Liquidated' });
    });
    contr.on('UserLoanCancelled', async (...args) => {
      await fetchLoans(contr, account);
      if (onTransaction) onTransaction({ hash: args[args.length - 1].transactionHash, type: 'Loan Cancelled' });
    });
    contr.on('TokenAdded', async (...args) => {
      await fetchSupportedTokens(contr);
    });

    return () => {
      contr.removeAllListeners();
    };
  }, [account, onTransaction, fetchSupportedTokens]); // Přidána závislost fetchSupportedTokens

  useEffect(() => {
    if (provider && account) {
      const initContract = async () => {
        try {
          setLoading(true);
          setError('');
          const sign = await provider.getSigner();
          setSigner(sign);
          const contr = new ethers.Contract(CONTRACT_ADDRESS, abi, sign);
          setContract(contr);
          await fetchSupportedTokens(contr);
          await fetchLoans(contr, account);
          listenToEvents(contr);
        } catch (error) {
          console.error('Contract initialization failed:', error);
          setError('Failed to initialize Lending contract. Check console for details.');
        } finally {
          setLoading(false);
        }
      };
      initContract();
    }
  }, [provider, account, listenToEvents, fetchSupportedTokens]); // Přidána závislost fetchSupportedTokens

  const fetchLoans = async (contr, acc) => {
    try {
      const lenderLoan = await contr.userLoans(acc);
      setLenderLoans(lenderLoan.lender === ZeroAddress ? [] : [lenderLoan]);
    } catch (err) {
      setLenderLoans([]);
    }

    try {
      const borrowerLoan = await contr.getLoanByBorrower(acc);
      setBorrowerLoans(borrowerLoan.borrower === ZeroAddress ? [] : [borrowerLoan]);
    } catch (err) {
      setBorrowerLoans([]);
    }

    try {
      const filter = contr.filters.UserLoanCreated();
      const events = await contr.queryFilter(filter, -5000);
      const loans = [];
      for (const ev of events) {
        try {
          const loan = await contr.userLoans(ev.args.lender);
          if (!loan.isActive && loan.lender !== acc && loan.lender !== ZeroAddress) {
            loans.push({ ...loan, createdBlock: ev.blockNumber });
          }
        } catch {}
      }
      setAvailableLoans(loans);
    } catch (err) {
      setAvailableLoans([]);
    }
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    if (!contract || loading) return;
    try {
      setLoading(true);
      setError('');
      const decimals = supportedTokens.find(t => t.tokenAddress === loanToken)?.tokenDecimals || 18;
      const amount = ethers.parseUnits(loanAmount, decimals);
      if (amount <= 0n) throw new Error('Loan amount must be greater than 0');

      if (loanToken && loanToken !== ZeroAddress) {
        const erc20Abi = ['function approve(address spender, uint256 amount) external returns (bool)'];
        const token = new ethers.Contract(loanToken, erc20Abi, signer);
        const approveTx = await token.approve(CONTRACT_ADDRESS, amount);
        await approveTx.wait();
      }

      const interestRateBig = (interestRate || 0) + 'n';
      const loanDurationInDays = parseFloat(loanDuration) || 0;
      const loanDurationInSeconds = Math.floor(loanDurationInDays * 86400); // Convert days to seconds
      const loanDurationBig = loanDurationInSeconds + 'n';
      const collateralPercentageBig = (collateralPercentage || 100) + 'n';

      const tx = await contract.createUserLoan(
        loanToken || ZeroAddress,
        collateralToken || ZeroAddress,
        amount,
        interestRateBig,
        loanDurationBig,
        collateralPercentageBig,
        { value: loanToken === ZeroAddress ? amount : 0n }
      );
      await tx.wait();
      onTransaction?.({ hash: tx.hash, type: 'Create Loan' });
      fetchLoans(contract, account);
      setLoanAmount(''); setInterestRate(''); setLoanDuration(''); setCollateralPercentage('');
    } catch (err) {
      setError(err.reason || err.message || 'Failed to create loan');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateLoan = async (e) => {
    e.preventDefault();
    if (!contract || loading) return;
    try {
      setLoading(true);
      setError('');
      const amount = ethers.parseUnits(collateralAmount, supportedTokens.find(t => t.tokenAddress === collateralToken)?.tokenDecimals || 18);
      if (amount <= 0n) throw new Error('Collateral amount must be greater than 0');

      if (collateralToken && collateralToken !== ZeroAddress) {
        const erc20Abi = ['function approve(address spender, uint256 amount) external returns (bool)'];
        const token = new ethers.Contract(collateralToken, erc20Abi, signer);
        const approveTx = await token.approve(CONTRACT_ADDRESS, amount);
        await approveTx.wait();
      }

      const tx = await contract.activateUserLoan(
        lenderAddress || ZeroAddress,
        amount,
        { value: collateralToken === ZeroAddress ? amount : 0n }
      );
      await tx.wait();
      onTransaction?.({ hash: tx.hash, type: 'Activate Loan' });
      fetchLoans(contract, account);
      setCollateralAmount(''); setLenderAddress('');
    } catch (err) {
      setError(err.reason || err.message || 'Failed to activate loan');
    } finally {
      setLoading(false);
    }
  };

  const handleRepayLoan = async () => {
    if (!contract || !borrowerLoans[0] || loading) return;
    try {
      setLoading(true);
      setError('');
      const loan = borrowerLoans[0];
      const amount = loan.loanAmount;

      if (loan.loanToken !== ZeroAddress) {
        const erc20Abi = ['function approve(address spender, uint256 amount) external returns (bool)'];
        const token = new ethers.Contract(loan.loanToken, erc20Abi, signer);
        const approveTx = await token.approve(CONTRACT_ADDRESS, amount);
        await approveTx.wait();
      }

      const tx = await contract.repayUserLoan({ value: loan.loanToken === ZeroAddress ? amount : 0n });
      await tx.wait();
      onTransaction?.({ hash: tx.hash, type: 'Repay Loan' });
      fetchLoans(contract, account);
    } catch (err) {
      setError(err.reason || err.message || 'Failed to repay loan');
    } finally {
      setLoading(false);
    }
  };

  const handleLiquidateLoan = async (borrower) => {
    if (!contract || loading) return;
    try {
      setLoading(true);
      setError('');
      const tx = await contract.liquidateUserLoanByLender(borrower);
      await tx.wait();
      onTransaction?.({ hash: tx.hash, type: 'Liquidate Loan' });
      fetchLoans(contract, account);
    } catch (err) {
      setError(err.reason || err.message || 'Failed to liquidate loan');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLoan = async () => {
    if (!contract || loading) return;
    try {
      setLoading(true);
      setError('');
      const tx = await contract.cancelUserLoan();
      await tx.wait();
      onTransaction?.({ hash: tx.hash, type: 'Cancel Loan' });
      fetchLoans(contract, account);
    } catch (err) {
      setError(err.reason || err.message || 'Failed to cancel loan');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, decimals) => ethers.formatUnits(amount, decimals || 18);
  const getTokenSymbol = (address) => supportedTokens.find(t => t.tokenAddress === address)?.symbol || address.slice(-4);
  const isExpired = (loan) => loan.isActive && (Number(loan.startTime) + Number(loan.loanDuration)) < Math.floor(Date.now() / 1000);

  if (loading) return <div className="main-content loading"><h2>Lending <FaHandHoldingUsd /></h2><p>Loading...</p></div>;

  return (
    <div className="main-content">
      <h2><FaHandHoldingUsd className="icon" /> Lending Platform</h2>
      {error && <p className="error">{error}</p>}
      
      <div className="lending-tabs">
        <button className={activeTab === 'create' ? 'active-tab' : ''} onClick={() => setActiveTab('create')}>
          <FaPlus className="icon" /> Create Loan
        </button>
        <button className={activeTab === 'borrower-loans' ? 'active-tab' : ''} onClick={() => setActiveTab('borrower-loans')}>
          <FaUserPlus className="icon" /> My Loans
        </button>
        <button className={activeTab === 'lender-loans' ? 'active-tab' : ''} onClick={() => setActiveTab('lender-loans')}>
          <FaHandHoldingUsd className="icon" /> Lender Dashboard
        </button>
        <button className={activeTab === 'available-loans' ? 'active-tab' : ''} onClick={() => setActiveTab('available-loans')}>
          <FaSearchDollar className="icon" /> Available Offers
        </button>
      </div>

      <div className="lending-container">
        {activeTab === 'create' && (
          <form onSubmit={handleCreateLoan} className="lending-form">
            <div className="form-group">
              <label>Loan Token</label>
              <select
                value={loanToken}
                onChange={(e) => setLoanToken(e.target.value)}
                required
                style={{ color: 'black', backgroundColor: 'white' }} // Inline CSS pro černý text a bílé pozadí
              >
                <option value="" style={{ color: 'black', backgroundColor: 'white' }}>Select Token</option>
                {supportedTokens.map(t => (
                  <option
                    key={t.tokenAddress}
                    value={t.tokenAddress}
                    style={{ color: 'black', backgroundColor: 'white' }}
                  >
                    {t.symbol}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Collateral Token</label>
              <select
                value={collateralToken}
                onChange={(e) => setCollateralToken(e.target.value)}
                required
                style={{ color: 'black', backgroundColor: 'white' }}
              >
                <option value="" style={{ color: 'black', backgroundColor: 'white' }}>Select Token</option>
                {supportedTokens.map(t => (
                  <option
                    key={t.tokenAddress}
                    value={t.tokenAddress}
                    style={{ color: 'black', backgroundColor: 'white' }}
                  >
                    {t.symbol}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Amount</label>
              <input type="number" step="0.0001" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} placeholder="1.0" required />
            </div>
            <div className="form-group">
              <label>Interest Rate (%)</label>
              <input type="number" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} placeholder="5" required />
            </div>
            <div className="form-group">
              <label>Duration (days)</label>
              <input type="number" step="0.1" value={loanDuration} onChange={(e) => setLoanDuration(e.target.value)} placeholder="1" required />
            </div>
            <div className="form-group">
              <label>Collateral % (≥100)</label>
              <input type="number" min="100" value={collateralPercentage} onChange={(e) => setCollateralPercentage(e.target.value)} placeholder="150" required />
            </div>
            <div className="lending-submit">
              <button type="submit" disabled={loading}>
                {loading ? <FaExclamationTriangle /> : <FaPlus />} {loading ? 'Creating...' : 'Create Offer'}
              </button>
            </div>
            <div className="lending-info">
              <FaCheckCircle /> <strong>Note:</strong> Owner fee is 0.01%. For ERC20, approve contract first (automated).
            </div>
          </form>
        )}

        {activeTab === 'borrower-loans' && (
          <div className="lending-container">
            <h3><FaUserPlus className="icon" /> My Borrower Loans</h3>
            {borrowerLoans.length === 0 ? (
              <div className="lending-no-loans">
                <FaSearchDollar /> No active loans. Create or activate one!
              </div>
            ) : (
              borrowerLoans.map((loan, i) => (
                <div key={i} className="loan-item">
                  <h4>Loan #{i + 1} <span className={`loan-status-badge ${isExpired(loan) ? 'loan-status-expired' : 'loan-status-active'}`}>
                    {isExpired(loan) ? 'Expired' : 'Active'}
                  </span></h4>
                  <p><span>Token:</span> {getTokenSymbol(loan.loanToken)}</p>
                  <p><span>Amount:</span> {formatAmount(loan.loanAmount, loan.loanTokenDecimals)}</p>
                  <p><span>Collateral:</span> {formatAmount(loan.collateralAmount, loan.collateralTokenDecimals)}</p>
                  <p><span>Interest:</span> {loan.interestRate}%</p>
                  <p><span>Lender:</span> {loan.lender.slice(0, 6)}...{loan.lender.slice(-4)}</p>
                  {!isExpired(loan) && (
                    <button onClick={handleRepayLoan} disabled={loading} className="lending-submit button">
                      <FaMoneyCheckAlt /> Repay Now
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'lender-loans' && (
          <div className="lending-container">
            <h3><FaHandHoldingUsd className="icon" /> My Lender Loans</h3>
            {lenderLoans.length === 0 ? (
              <div className="lending-no-loans">No lender offers. Create one!</div>
            ) : (
              lenderLoans.map((loan, i) => (
                <div key={i} className="loan-item">
                  <h4>Offer #{i + 1} <span className={`loan-status-badge ${loan.isActive ? (isExpired(loan) ? 'loan-status-expired' : 'loan-status-active') : 'loan-status-pending'}`}>
                    {loan.isActive ? (isExpired(loan) ? 'Expired' : 'Active') : 'Pending'}
                  </span></h4>
                  <p><span>Token:</span> {getTokenSymbol(loan.loanToken)}</p>
                  <p><span>Amount:</span> {formatAmount(loan.loanAmount, loan.loanTokenDecimals)}</p>
                  <p><span>Interest:</span> {loan.interestRate}%</p>
                  <p><span>Collateral Req.:</span> {loan.collateralPercentage}%</p>
                  {loan.isActive && loan.borrower !== ZeroAddress && (
                    <p><span>Borrower:</span> {loan.borrower.slice(0, 6)}...{loan.borrower.slice(-4)}</p>
                  )}
                  {!loan.isActive && (
                    <button onClick={handleCancelLoan} disabled={loading}>Cancel Offer</button>
                  )}
                  {isExpired(loan) && loan.isActive && (
                    <button onClick={() => handleLiquidateLoan(loan.borrower)} disabled={loading} className="error button">Liquidate</button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'available-loans' && (
          <div className="lending-container">
            <h3><FaSearchDollar className="icon" /> Available Loan Offers</h3>
            {availableLoans.length === 0 ? (
              <div className="lending-no-loans">No available offers. Be the first to create one!</div>
            ) : (
              availableLoans.map((loan, i) => (
                <div key={i} className="loan-item">
                  <h4>Offer from {loan.lender.slice(0, 6)}...{loan.lender.slice(-4)} <span className="loan-status-badge loan-status-pending">Available</span></h4>
                  <p><span>Token:</span> {getTokenSymbol(loan.loanToken)}</p>
                  <p><span>Amount:</span> {formatAmount(loan.loanAmount, loan.loanTokenDecimals)}</p>
                  <p><span>Interest:</span> {loan.interestRate}%</p>
                  <p><span>Duration:</span> {loan.loanDuration} seconds</p>
                  <p><span>Collateral Req.:</span> {loan.collateralPercentage}%</p>
                  {loan.lender === account ? (
                    <button onClick={handleCancelLoan} disabled={loading}>Cancel My Offer</button>
                  ) : (
                    <form onSubmit={handleActivateLoan}>
                      <input type="hidden" value={loan.lender} onChange={(e) => setLenderAddress(e.target.value)} />
                      <div className="form-group">
                        <label>Lender: {loan.lender.slice(0, 6)}...{loan.lender.slice(-4)}</label>
                        <input
                          type="number"
                          step="0.0001"
                          value={collateralAmount}
                          onChange={(e) => setCollateralAmount(e.target.value)}
                          placeholder="Collateral Amount"
                          required
                        />
                      </div>
                      <button type="submit" disabled={loading}>Activate & Borrow</button>
                    </form>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Lending;